import { z } from "zod";
import { BusinessDate, Instant, Note } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";

/**
 * Finance & Invoicing contracts (architecture §7.3, §7.9, §10, DL-033, DL-035, DL-036).
 *
 * Money is ALWAYS integer rial (no floating-point amounts, no fractions).
 * Strict objects: no kitchen upgrades or internal portion fields may EVER exist here (AGENTS.md §3.4).
 */

export const InvoiceBasis = z.enum(["ordered", "delivered"]);
export type InvoiceBasis = z.infer<typeof InvoiceBasis>;

export const InvoiceStatus = z.enum(["draft", "issued"]);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

export const DiscountScope = z.enum(["line", "invoice"]);
export type DiscountScope = z.infer<typeof DiscountScope>;

export const Discount = z
  .object({
    id: Id,
    scope: DiscountScope,
    /** Line ID if scope is "line". */
    lineId: Id.nullable().default(null),
    reason: z.string().min(1),
    amount: Rial,
  })
  .strict();
export type Discount = z.infer<typeof Discount>;

export const InvoiceLine = z
  .object({
    id: Id,
    orderId: Id,
    orderLineId: Id,
    menuItemId: Id,
    tierId: Id.nullable(),
    businessDate: BusinessDate,
    /** Both quantities are always recorded and shown (DL-035). */
    orderedQuantity: z.int().nonnegative(),
    deliveredQuantity: z.int().nonnegative().nullable(),
    /** The quantity used for calculating line subtotal based on invoice basis. */
    billedQuantity: z.int().nonnegative(),
    unitPrice: Rial,
    /** billedQuantity * unitPrice */
    subtotal: Rial,
    /** Sum of all line discounts for this line */
    discountAmount: Rial,
    /** subtotal - discountAmount */
    totalAmount: Rial,
    /** Whether this line represents a companion charged separately */
    isCompanion: z.boolean().default(false),
    note: Note.default(""),
  })
  .strict();
export type InvoiceLine = z.infer<typeof InvoiceLine>;

export const NoteType = z.enum(["credit", "debit"]);
export type NoteType = z.infer<typeof NoteType>;

/**
 * Debit or Credit Note issued when an issued invoice is revised.
 * - Credit Note: new revision total < old revision total (kitchen owes customer / credit).
 * - Debit Note: new revision total > old revision total (customer owes kitchen / extra charge).
 */
export const DebitCreditNote = z
  .object({
    id: Id,
    noteNumber: z.string().min(1),
    type: NoteType,
    invoiceId: Id,
    invoiceNumber: z.string().min(1),
    revision: z.int().min(2),
    amount: Rial,
    reason: z.string().min(1),
    issuedAt: Instant,
    problemId: Id.nullable().optional(),
  })
  .strict();
export type DebitCreditNote = z.infer<typeof DebitCreditNote>;

export const Invoice = z
  .object({
    id: Id,
    organizationId: Id,
    orderingUnitIds: z.array(Id),
    periodStart: BusinessDate,
    periodEnd: BusinessDate,
    fiscalYear: z.int().min(1300).max(1500),
    /** E.g. "1405-0001", and for revisions "1405-0001/2", "1405-0001/3". */
    number: z.string().min(1),
    /** 1 for original issued invoice, 2 for revision 2, etc. Drafts can be 0 or 1. */
    revision: z.int().min(1),
    status: InvoiceStatus,
    basis: InvoiceBasis,
    lines: z.array(InvoiceLine),
    invoiceDiscounts: z.array(Discount),
    totalOrderedQuantity: z.int().nonnegative(),
    totalDeliveredQuantity: z.int().nonnegative(),
    totalBilledQuantity: z.int().nonnegative(),
    subtotal: Rial,
    totalLineDiscounts: Rial,
    totalInvoiceDiscounts: Rial,
    totalDiscount: Rial,
    totalAmount: Rial,
    notes: z.string().default(""),
    createdAt: Instant,
    issuedAt: Instant.nullable(),
  })
  .strict();
export type Invoice = z.infer<typeof Invoice>;

/** Reported when an order line does not have a price applied. Prices are NEVER invented. */
export const UnpricedLineReport = z
  .object({
    orderId: Id,
    orderLineId: Id,
    menuItemId: Id,
    tierId: Id.nullable(),
    businessDate: BusinessDate,
    orderedQuantity: z.int().nonnegative(),
    reason: z.string(),
  })
  .strict();
export type UnpricedLineReport = z.infer<typeof UnpricedLineReport>;

/** Input order line structure passed to the invoicing engine. */
export const InvoicingOrderLineInput = z
  .object({
    orderId: Id,
    orderLineId: Id,
    menuItemId: Id,
    tierId: Id.nullable(),
    businessDate: BusinessDate,
    orderingUnitId: Id,
    orderedQuantity: z.int().nonnegative(),
    deliveredQuantity: z.int().nonnegative().nullable(),
    unitPrice: Rial.nullable(),
    isCompanion: z.boolean().default(false),
    note: Note.default(""),
  })
  .strict();
export type InvoicingOrderLineInput = z.infer<typeof InvoicingOrderLineInput>;

/** Input for generating or updating an invoice for an organization over a period. */
export const InvoiceGenerateInput = z
  .object({
    id: Id.optional(),
    organizationId: Id,
    orderingUnitIds: z.array(Id).optional(),
    periodStart: BusinessDate,
    periodEnd: BusinessDate,
    fiscalYear: z.int().min(1300).max(1500),
    basis: InvoiceBasis,
    orderLines: z.array(InvoicingOrderLineInput),
    discounts: z.array(Discount).optional(),
    notes: z.string().optional(),
    asDraft: z.boolean().optional(),
  })
  .strict();
export type InvoiceGenerateInput = z.infer<typeof InvoiceGenerateInput>;

/** Result of generating an invoice, including any unpriced lines that could not be billed. */
export const InvoiceGenerationResult = z
  .object({
    invoice: Invoice,
    unpricedLines: z.array(UnpricedLineReport),
  })
  .strict();
export type InvoiceGenerationResult = z.infer<typeof InvoiceGenerationResult>;

/** Result of revising an already-issued invoice. */
export const InvoiceRevisionResult = z
  .object({
    previousInvoice: Invoice,
    newRevision: Invoice,
    note: DebitCreditNote.nullable(),
    unpricedLines: z.array(UnpricedLineReport),
  })
  .strict();
export type InvoiceRevisionResult = z.infer<typeof InvoiceRevisionResult>;

/** Impact preview for re-pricing (architecture §7.3). */
export const RePricingImpactPreview = z
  .object({
    affectedDraftCount: z.int().nonnegative(),
    affectedIssuedCount: z.int().nonnegative(),
    draftsDiff: Rial,
    issuedRevisionsDiff: Rial,
    totalNetDifference: Rial,
    draftChanges: z.array(
      z.object({
        invoiceId: Id,
        oldTotal: Rial,
        newTotal: Rial,
        diff: Rial,
      }),
    ),
    issuedRevisions: z.array(
      z.object({
        invoiceId: Id,
        invoiceNumber: z.string(),
        currentRevision: z.int(),
        oldTotal: Rial,
        newTotal: Rial,
        diff: Rial,
        noteType: NoteType.nullable(),
      }),
    ),
  })
  .strict();
export type RePricingImpactPreview = z.infer<typeof RePricingImpactPreview>;

// ---------------------------------------------------------------------------------------------------------------
// Payments & Receivables (Req §10.3)

export const PaymentMethod = z.enum(["cash", "card", "bank-transfer", "cheque"]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const ChequeStatus = z.enum(["pending", "cleared", "bounced"]);
export type ChequeStatus = z.infer<typeof ChequeStatus>;

export const PaymentAllocation = z
  .object({
    id: Id,
    paymentId: Id,
    invoiceId: Id,
    amount: Rial,
    allocatedAt: Instant,
  })
  .strict();
export type PaymentAllocation = z.infer<typeof PaymentAllocation>;

export const PaymentRecord = z
  .object({
    id: Id,
    version: z.int().min(0).default(0),
    organizationId: Id,
    amount: Rial,
    method: PaymentMethod,
    referenceNumber: z.string().default(""),
    paidAt: BusinessDate,
    notes: z.string().default(""),
    // Cheque specifics
    chequeNumber: z.string().nullable().optional(),
    chequeBank: z.string().nullable().optional(),
    chequeDueDate: BusinessDate.nullable().optional(),
    chequeStatus: ChequeStatus.nullable().optional(),
    bouncedAt: Instant.nullable().optional(),
    bouncedReason: z.string().nullable().optional(),
    allocations: z.array(PaymentAllocation).default([]),
    unallocatedAmount: Rial,
    createdAt: Instant,
  })
  .strict();
export type PaymentRecord = z.infer<typeof PaymentRecord>;

export const PaymentInput = z
  .object({
    organizationId: Id,
    amount: Rial,
    method: PaymentMethod,
    referenceNumber: z.string().optional(),
    paidAt: BusinessDate,
    notes: z.string().optional(),
    chequeNumber: z.string().optional(),
    chequeBank: z.string().optional(),
    chequeDueDate: BusinessDate.optional(),
    allocations: z
      .array(
        z.object({
          invoiceId: Id,
          amount: Rial,
        }),
      )
      .optional(),
  })
  .strict();
export type PaymentInput = z.infer<typeof PaymentInput>;

export const ChequeActionInput = z
  .object({
    version: z.int().min(0),
    action: z.enum(["clear", "bounce"]),
    reason: z.string().optional(),
  })
  .strict();
export type ChequeActionInput = z.infer<typeof ChequeActionInput>;

export const AllocatePaymentInput = z
  .object({
    paymentId: Id,
    version: z.int().min(0),
    allocations: z.array(
      z.object({
        invoiceId: Id,
        amount: Rial,
      }),
    ),
  })
  .strict();
export type AllocatePaymentInput = z.infer<typeof AllocatePaymentInput>;

export const OrganizationBasisSetting = z
  .object({
    organizationId: Id,
    basis: InvoiceBasis,
  })
  .strict();
export type OrganizationBasisSetting = z.infer<typeof OrganizationBasisSetting>;

export const AgingBuckets = z
  .object({
    current0to30: Rial,
    days31to60: Rial,
    days61to90: Rial,
    days90Plus: Rial,
    totalOverdue: Rial,
  })
  .strict();
export type AgingBuckets = z.infer<typeof AgingBuckets>;

export const StatementTransaction = z
  .object({
    date: BusinessDate,
    type: z.enum(["invoice", "payment", "credit-note", "debit-note", "cheque-bounced"]),
    referenceId: Id,
    referenceNumber: z.string(),
    description: z.string(),
    debit: Rial,
    credit: Rial,
    balance: Rial,
  })
  .strict();
export type StatementTransaction = z.infer<typeof StatementTransaction>;

export const CustomerStatement = z
  .object({
    organizationId: Id,
    organizationName: z.string(),
    asOfDate: BusinessDate,
    totalInvoiced: Rial,
    totalPaid: Rial,
    totalCreditNotes: Rial,
    totalDebitNotes: Rial,
    balance: Rial,
    unallocatedCredit: Rial,
    aging: AgingBuckets,
    transactions: z.array(StatementTransaction),
  })
  .strict();
export type CustomerStatement = z.infer<typeof CustomerStatement>;

export const HighDebtThresholdInput = z
  .object({
    thresholdRials: Rial,
  })
  .strict();
export type HighDebtThresholdInput = z.infer<typeof HighDebtThresholdInput>;

export const CreateInvoiceDraftInput = z
  .object({
    organizationId: Id,
    orderingUnitIds: z.array(Id).optional(),
    periodStart: BusinessDate,
    periodEnd: BusinessDate,
    fiscalYear: z.int().min(1300).max(1500).optional(),
    basis: InvoiceBasis.optional(),
    notes: z.string().optional(),
    discounts: z.array(Discount).optional(),
  })
  .strict();
export type CreateInvoiceDraftInput = z.infer<typeof CreateInvoiceDraftInput>;

export const RecomputeDraftInput = z
  .object({
    version: z.int().min(0),
    basisOverride: InvoiceBasis.optional(),
    discounts: z.array(Discount).optional(),
  })
  .strict();
export type RecomputeDraftInput = z.infer<typeof RecomputeDraftInput>;

export const IssueInvoiceInput = z
  .object({
    version: z.int().min(0),
    notes: z.string().optional(),
  })
  .strict();
export type IssueInvoiceInput = z.infer<typeof IssueInvoiceInput>;

export const ReviseInvoiceInput = z
  .object({
    version: z.int().min(0),
    reason: z.string().min(1),
    discounts: z.array(Discount).optional(),
    basisOverride: InvoiceBasis.optional(),
    notes: z.string().optional(),
    problemId: Id.optional(),
  })
  .strict();
export type ReviseInvoiceInput = z.infer<typeof ReviseInvoiceInput>;

export const RePricingPreviewInput = z
  .object({
    organizationId: Id.optional(),
    periodStart: BusinessDate,
    periodEnd: BusinessDate,
  })
  .strict();
export type RePricingPreviewInput = z.infer<typeof RePricingPreviewInput>;
